import { useContext } from 'react';

import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { InfiniteData } from '@tanstack/react-query';
import type { CacheRequestConfig } from 'axios-cache-interceptor';
import { chunk } from 'lodash';

import { ConfigContext } from '../../../../aurr/contexts/ConfigContext';
import LoginContext from '../../../../aurr/contexts/LoginContext';
import { buildCacheKey, getAxiosInstance } from '../../../axiosCache';
import {
  REPLIES_MUTATE_POST_KEY,
  REPLIES_QUERY_KEY,
  TOPIC_QUERY_KEY,
  USER_QUERY_KEY,
} from '../../constants';
import {
  KhorosPaginatedProps,
  KhorosTopicProps,
} from '../../interface/khoros.interface';
import {
  KhorosRepliesGetSchema,
  KhorosReplyItemSchema,
} from '../../type/khoros-replies.type';
import { KhorosTopicSchema } from '../../type/khoros-topic.type';
import { KhorosUserSchema } from '../../type/khoros-user.type';
import { useKhorosUser } from '../useKhorosUser';

export const useKhorosPost = ({
  topicId,
  baseURL,
  pageSize,
}: KhorosTopicProps & KhorosPaginatedProps) => {
  const { KHOROS_API } = useContext(ConfigContext);
  const { access_token, userProfile } = useContext(LoginContext);
  const sub = userProfile?.sub;

  const axiosConfig: CacheRequestConfig = {
    baseURL: baseURL || KHOROS_API,
    headers: {
      ...(access_token && { authorization: `Bearer ${access_token}` }),
    },
    cache: {
      update: {
        [buildCacheKey([REPLIES_QUERY_KEY, topicId])]: 'delete',
        [buildCacheKey([USER_QUERY_KEY, sub])]: 'delete',
        [buildCacheKey([TOPIC_QUERY_KEY, topicId, sub])]: 'delete',
      },
    },
  };

  const { data: userResponse } = useKhorosUser({
    baseURL,
  });

  const api = getAxiosInstance();
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: [REPLIES_MUTATE_POST_KEY, { topicId, sub }],
    mutationFn: async body => {
      const response = await api.post(
        `/services/v2/community/khoros/replies?id=${topicId}`,
        {
          body,
        },
        axiosConfig
      );
      return response?.data || undefined;
    },
    onMutate: async (body: string) => {
      // Cancel any outgoing refetches (so they don't overwrite our optimistic update)
      await queryClient.cancelQueries({
        queryKey: [REPLIES_QUERY_KEY, { topicId }],
      });
      await queryClient.cancelQueries({
        queryKey: [TOPIC_QUERY_KEY, { topicId, sub }],
      });
      await queryClient.cancelQueries({ queryKey: [USER_QUERY_KEY, { sub }] });

      // Snapshot the previous value
      const savedReplies = queryClient.getQueryData([
        REPLIES_QUERY_KEY,
        { topicId },
      ]);

      const savedTopic = queryClient.getQueryData([
        TOPIC_QUERY_KEY,
        { topicId, sub },
      ]);

      const savedUser = queryClient.getQueryData([USER_QUERY_KEY, { sub }]);

      // Optimistically update to the new value
      const placeholderComment: KhorosReplyItemSchema = {
        kh_post_id: 'temporary-value',
        kh_post_time: new Date().toString(),
        kh_body: `${body}`,
        kh_author_user_id: userResponse?.kh_user_id || 'temporary-value',
        kh_author_user_login: userResponse?.kh_user_login || 'temporary-value',
        kh_author_avatar: userResponse?.kh_user_avatar || 'temporary-value',
        kh_kudos_count: 0,
        kh_total_replies_count: 0,
        kh_author_sso_id: sub || '',
      };

      queryClient.setQueryData<InfiniteData<KhorosRepliesGetSchema>>(
        [REPLIES_QUERY_KEY, { topicId }],
        old => {
          const placeholderPage = {
            status: 'success',
            kh_replies: [],
            pageSize,
            cursor: 0,
            has_next_page: false,
            statuscode: 200,
          };

          const newResponse = {
            pages: [...(old?.pages || [placeholderPage])],
            pageParams: old?.pageParams || [{ 0: undefined }],
          };

          const { pages } = newResponse;

          const flatPages = pages.flatMap(page => page.kh_replies);
          // Remove the oldest comment from view to make room for the new comment.
          if (pages.length > 0 || pages[0].kh_replies.length === pageSize) {
            flatPages.pop();
          }
          flatPages.unshift(placeholderComment);

          newResponse.pages = chunk(flatPages, pageSize).map((page, idx) => {
            return {
              ...newResponse.pages[idx],
              kh_replies: page,
            };
          });
          return newResponse;
        }
      );

      queryClient.setQueryData<KhorosTopicSchema>(
        [TOPIC_QUERY_KEY, { topicId, sub }],
        old => {
          if (!old) {
            return old;
          }
          return {
            ...old,
            kh_total_replies_count: old.kh_total_replies_count + 1,
          };
        }
      );

      queryClient.setQueryData<KhorosUserSchema | undefined>(
        [USER_QUERY_KEY, { sub }],
        old => {
          if (!old) {
            return old;
          }
          return {
            ...old,
            kh_messages_count: old.kh_messages_count + 1,
          };
        }
      );

      return { savedReplies, savedTopic, savedUser };
    },
    onError: (err, newReply, context) => {
      queryClient.setQueryData(
        [REPLIES_QUERY_KEY, { topicId }],
        context?.savedReplies
      );

      queryClient.setQueryData(
        [TOPIC_QUERY_KEY, { topicId, access_token }],
        context?.savedTopic
      );

      queryClient.setQueryData([USER_QUERY_KEY, { sub }], context?.savedUser);
    },
    onSettled: variables => {
      queryClient.invalidateQueries({
        queryKey: [REPLIES_QUERY_KEY, { topicId }],
      });
      queryClient.invalidateQueries({
        queryKey: [TOPIC_QUERY_KEY, { topicId, sub }],
      });
      queryClient.invalidateQueries({
        queryKey: [
          REPLIES_QUERY_KEY,
          { topicId, commentId: variables?.data?.kh_post_id },
        ],
      });
      queryClient.invalidateQueries({ queryKey: [USER_QUERY_KEY, { sub }] });
    },
  });
};
